import { isInboundClientEmail, parseCustomerEmail } from "./emailCompose";

function filenameFromUrl(url, fallback = "Anexo") {
  const href = String(url || "").trim();
  if (!href) return fallback;
  try {
    const path = new URL(href, "https://xpro.me").pathname;
    const last = decodeURIComponent(path.split("/").filter(Boolean).pop() || "");
    if (last && /\.[a-z0-9]{2,8}$/i.test(last)) return last.slice(0, 180);
  } catch {
    /* ignore */
  }
  return fallback;
}

function messageTimeMs(message) {
  const raw = message?.createdAt || message?.timestamp || message?.updatedAt || message?.lastUpdate;
  if (!raw) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw.getTime();
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(trimmed)) {
      const d = new Date(`${trimmed.replace(" ", "T")}-03:00`);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    }
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

const EMAIL_ORIGINS = new Set([9, 30, 35]);
const MEDIA_TYPES = new Set(["image", "video", "audio", "file", "document"]);
const ATTACHMENT_WINDOW_MS = 5 * 60 * 1000;

function parseJsonWhatsapp(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function mediaUrlOfMessage(message) {
  if (!message) return "";
  const doc = message.document;
  const docUrl = doc && typeof doc === "object" ? doc.url || doc.link || "" : typeof doc === "string" ? doc : "";
  return String(
    message.image || message.video || message.audio || message.arquivo || message.imagem || docUrl || ""
  );
}

function hasWhatsappTransportMeta(message) {
  if (String(message?.id_msg_whatsapp || "").trim()) return true;
  if (message?.status_whatsapp != null && String(message.status_whatsapp).trim() !== "") return true;
  const j = parseJsonWhatsapp(message?.json_whatsapp);
  if (!j || typeof j !== "object") return false;
  if (j.xpro_email) return false;
  if (j.xpro_transport === "webchat" || j.xpro_transport === "twilio_voice") return false;
  return true;
}

function haystack(message) {
  return `${mediaUrlOfMessage(message)} ${message?.text || message?.mensagem || ""}`;
}

/** Anexo órfão do Mailgun: mídia sem canal/WhatsApp, path /midias/anexos/. */
export function looksLikeOrphanEmailAttachment(message) {
  if (!message || message.email?.direction === "outbound") return false;
  if (hasWhatsappTransportMeta(message)) return false;

  const origin = Number(message.originTipo ?? message.messageChannelTipo);
  const type = String(message.type || "").toLowerCase();
  const urlHay = haystack(message);
  const anexoPath = /\/midias\/anexos\//i.test(urlHay);
  const isMedia =
    MEDIA_TYPES.has(type) ||
    Boolean(message.image || message.video || message.audio || message.document?.url) ||
    ((type === "note" || type === "xpro") && anexoPath && /anexo/i.test(urlHay));

  if (!isMedia) return false;
  if (EMAIL_ORIGINS.has(origin)) return true;

  const channelId = message.messageChannelId ?? message.id_origem;
  const hasOrigin =
    channelId != null &&
    String(channelId).trim() !== "" &&
    Number(channelId) !== 0;
  if (hasOrigin) return false;

  const userId = Number(message.id_usuario);
  if (Number.isFinite(userId) && userId > 0) {
    const senderType = String(message.senderType || message.sender || "").toLowerCase();
    if (senderType === "agent" || senderType === "operator" || senderType === "user") return false;
  }

  return anexoPath;
}

export function isInboundEmailBody(message) {
  if (!message || message.email?.direction === "outbound") return false;
  if (isInboundClientEmail(message)) return true;
  if (message.emailOriginal && String(message.emailOriginal).trim()) return true;
  if (message.hasEmailOriginal) return true;
  const origin = Number(message.originTipo);
  if (EMAIL_ORIGINS.has(origin)) return true;
  const channel = `${message.channel || ""} ${message.channelName || ""}`;
  if (!/e-?mail/i.test(channel)) return false;
  const labeled = parseCustomerEmail(message);
  return Boolean(labeled?.subject || /assunto\s*:/i.test(String(message.text || "")));
}

function isWithinAttachmentWindow(parent, child) {
  const a = messageTimeMs(parent);
  const b = messageTimeMs(child);
  if (a != null && b != null) return Math.abs(b - a) <= ATTACHMENT_WINDOW_MS;
  const ida = Number(parent?.id);
  const idb = Number(child?.id);
  if (Number.isFinite(ida) && Number.isFinite(idb)) return Math.abs(idb - ida) < 30;
  return true;
}

export function attachmentDisplayName(url, fallback = "Anexo") {
  const name = filenameFromUrl(url, "");
  if (!name) return fallback;
  if (/^\d+anexo/i.test(name)) return fallback;
  return name;
}

export function toEmailAttachment(message) {
  const url = mediaUrlOfMessage(message);
  const type = String(message?.type || "").toLowerCase();
  const kind = MEDIA_TYPES.has(type)
    ? type
    : message?.image
      ? "image"
      : message?.video
        ? "video"
        : message?.audio
          ? "audio"
          : "file";
  const fallback = kind === "image" ? "Imagem" : kind === "video" ? "Vídeo" : kind === "audio" ? "Áudio" : "Anexo";
  return {
    id: message?.id,
    kind,
    url,
    name: attachmentDisplayName(url, fallback),
    sourceMessage: message,
  };
}

export function attachmentsFromOriginalHtml(html) {
  const raw = String(html || "");
  if (!raw) return [];
  const found = [];
  const seen = new Set();
  const re = /(?:href|src)=["']([^"']*\/midias\/anexos\/[^"']+)["']/gi;
  let match;
  while ((match = re.exec(raw))) {
    const url = match[1];
    if (!url || seen.has(url)) continue;
    seen.add(url);
    const lower = url.toLowerCase();
    const kind = /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(lower)
      ? "image"
      : /\.(mp4|mov|avi|mpeg|webm)(\?|$)/i.test(lower)
        ? "video"
        : /\.(mp3|caf|wav|m4a|ogg)(\?|$)/i.test(lower)
          ? "audio"
          : "file";
    const absUrl = url.startsWith("http")
      ? url
      : `https://xvision.xpro.me${url.startsWith("/") ? url : `/${url}`}`;
    found.push({
      id: `html-${found.length}-${absUrl}`,
      kind,
      url: absUrl,
      name: attachmentDisplayName(url, kind === "image" ? "Imagem" : "Anexo"),
      sourceMessage: kind === "image" ? { id: `html-${found.length}`, image: absUrl, type: "image" } : null,
    });
  }
  return found;
}

function urlKey(url) {
  return String(url || "")
    .replace(/^https?:\/\/[^/]+/i, "")
    .split("?")[0]
    .toLowerCase();
}

function mergeAttachments(fromRows, fromHtml) {
  const out = [];
  const seen = new Set();
  for (const item of [...fromRows, ...fromHtml]) {
    const key = urlKey(item?.url) || `id:${item?.id}`;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

/**
 * Agrupa anexos órfãos na mensagem de e-mail anterior (experiência tipo Gmail)
 * e remove as bolhas soltas da timeline.
 */
export function nestInboundEmailAttachments(messages) {
  const list = Array.isArray(messages) ? messages : [];
  if (!list.length) return list;

  const hidden = new Set();
  const attachmentsByParent = new Map();

  for (let i = 0; i < list.length; i += 1) {
    const parent = list[i];
    if (!isInboundEmailBody(parent)) continue;

    const rowAttachments = [];
    for (let j = i + 1; j < list.length; j += 1) {
      const child = list[j];
      if (child?.id != null && hidden.has(child.id)) continue;
      if (!looksLikeOrphanEmailAttachment(child) || !isWithinAttachmentWindow(parent, child)) break;
      rowAttachments.push(toEmailAttachment(child));
      if (child?.id != null) hidden.add(child.id);
    }

    const htmlAttachments = attachmentsFromOriginalHtml(parent.emailOriginal || parent.text);
    const merged = mergeAttachments(rowAttachments, htmlAttachments);
    if (merged.length && parent?.id != null) {
      attachmentsByParent.set(parent.id, merged);
    }
  }

  return list
    .filter((msg) => msg?.id == null || !hidden.has(msg.id))
    .map((msg) => {
      const extra = attachmentsByParent.get(msg.id);
      if (!extra) return msg;
      return { ...msg, emailAttachments: extra };
    });
}
